import React, { useCallback, useEffect, useState } from "react";
import { BarChart3, Disc3, Download, Loader2, Mail, Music2, Users } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d", label: "Last 7 days" },
  { id: "month", label: "This month" },
  { id: "last_month", label: "Last month" },
];

function presetRange(p) {
  const now = new Date();
  const today = fmtDate(now);
  if (p === "yesterday") { const y = new Date(now.getTime() - 864e5); const s = fmtDate(y); return [s, s]; }
  if (p === "7d") return [fmtDate(new Date(now.getTime() - 6 * 864e5)), today];
  if (p === "month") return [fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)), today];
  if (p === "last_month") {
    return [fmtDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
            fmtDate(new Date(now.getFullYear(), now.getMonth(), 0))];
  }
  return [today, today];
}

function StatTile({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-ink-muted">
        <Icon className="h-3.5 w-3.5" />
        <span className="font-mono text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="mt-1.5 text-2xl font-display font-bold text-ink-primary">{value}</div>
    </div>
  );
}

export const TrackReportsTab = () => {
  const [preset, setPreset] = useState("7d");
  const [[start, end], setRange] = useState(() => presetRange("7d"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    api.get("/track-reports/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const toggleMonthly = async (v) => {
    try {
      const r = await api.put("/track-reports/settings", { monthly_email_enabled: v });
      setSettings(r.data);
      toast.success(v ? "Monthly report email is on — you'll get last month's CSVs on the 1st" : "Monthly report email turned off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };

  const emailNow = async () => {
    setEmailing(true);
    const tid = toast.loading("Building CSVs + sending email…");
    try {
      const r = await api.post("/track-reports/email-now");
      toast.success(`Report sent to ${r.data.to} — ${r.data.total_plays} plays`, { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed", { id: tid });
    } finally { setEmailing(false); }
  };

  const load = useCallback(async (s, e) => {
    setLoading(true);
    try {
      const r = await api.get(`/track-reports/report?start=${s}&end=${e}`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load report");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(start, end); }, [start, end, load]);

  const pickPreset = (p) => { setPreset(p); setRange(presetRange(p)); };
  const setStart = (v) => { setPreset(""); setRange([v, end]); };
  const setEnd = (v) => { setPreset(""); setRange([start, v]); };
  const csvUrl = (mode) => `${API_BASE}/track-reports/export.csv?start=${start}&end=${end}&mode=${mode}`;

  return (
    <div className="space-y-4" data-testid="np-track-reports">
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => pickPreset(p.id)}
            data-testid={`tr-preset-${p.id}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
              preset === p.id ? "border-brand-amber bg-brand-amber/15 text-brand-amber" : "border-line text-ink-muted hover:text-ink-secondary"
            }`}
          >
            {p.label}
          </button>
        ))}
        <div className="flex items-center gap-1.5 ml-auto text-xs">
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="tr-start-date"
                 className="rounded-md border border-line bg-bg-elevated px-2 py-1 font-mono text-xs focus:border-brand-amber focus:outline-none" />
          <span className="text-ink-muted">→</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="tr-end-date"
                 className="rounded-md border border-line bg-bg-elevated px-2 py-1 font-mono text-xs focus:border-brand-amber focus:outline-none" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile icon={BarChart3} label="Total plays" value={loading ? "…" : (data?.total_plays ?? 0)} testid="tr-stat-plays" />
        <StatTile icon={Disc3} label="Unique tracks" value={loading ? "…" : (data?.unique_tracks ?? 0)} testid="tr-stat-tracks" />
        <StatTile icon={Users} label="Unique artists" value={loading ? "…" : (data?.unique_artists ?? 0)} testid="tr-stat-artists" />
        <StatTile icon={Music2} label="Top artist" value={loading ? "…" : (data?.top_artists?.[0]?.artist || "—")} testid="tr-stat-top" />
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-bg-surface p-3 text-xs" data-testid="tr-email-row">
        <Mail className="h-3.5 w-3.5 text-brand-amber" />
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!settings?.monthly_email_enabled}
            onChange={(e) => toggleMonthly(e.target.checked)}
            data-testid="tr-monthly-email-toggle"
            className="accent-amber-500"
          />
          <span className="text-ink-secondary font-semibold">Email me the royalty report on the 1st of every month</span>
        </label>
        <span className="text-ink-muted hidden md:inline">— both CSVs attached, ready to file.</span>
        <button
          onClick={emailNow}
          disabled={emailing}
          data-testid="tr-email-now"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 font-semibold text-ink-secondary hover:text-ink-primary transition disabled:opacity-50"
        >
          {emailing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
          {emailing ? "Sending…" : "Email me this month now"}
        </button>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface">
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-4">
          <div>
            <h3 className="type-section-heading text-ink-primary">Played tracks</h3>
            <div className="text-[11px] text-ink-muted">Every spin the Now Playing poller detected, in station time — ready for royalty filings (SoundExchange, PRS, GEMA…).</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <a href={csvUrl("summary")} data-testid="tr-export-summary"
               className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-3 py-1.5 text-xs font-semibold text-brand-amber hover:bg-brand-amber/20 transition">
              <Download className="h-3 w-3" /> CSV — summary
            </a>
            <a href={csvUrl("plays")} data-testid="tr-export-plays"
               className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs font-semibold text-ink-secondary hover:text-ink-primary transition">
              <Download className="h-3 w-3" /> CSV — every spin
            </a>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-ink-muted text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Crunching plays…
          </div>
        ) : !data?.tracks?.length ? (
          <div className="p-10 text-center text-sm text-ink-muted" data-testid="tr-empty">
            No plays logged in this range. Set your stream URL in Rules &amp; Setup and enable the auto-poster — every detected song is logged here automatically.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">Artist</th>
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2 text-right">Plays</th>
                  <th className="px-4 py-2">Last played</th>
                </tr>
              </thead>
              <tbody data-testid="tr-table-body">
                {data.tracks.map((t, i) => (
                  <tr key={`${t.artist}|${t.title}`} className="border-b border-line/50 hover:bg-bg-elevated/50 transition">
                    <td className="px-4 py-2 font-mono text-xs text-ink-muted">{i + 1}</td>
                    <td className="px-4 py-2 text-ink-primary">{t.artist || <span className="text-ink-muted italic">unknown</span>}</td>
                    <td className="px-4 py-2 text-ink-secondary">{t.title}</td>
                    <td className="px-4 py-2 text-right font-mono font-bold text-brand-amber">{t.plays}</td>
                    <td className="px-4 py-2 font-mono text-xs text-ink-muted">{t.last_played ? new Date(t.last_played).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.tracks.length >= 500 && (
              <div className="p-3 text-center text-[11px] text-ink-muted">Showing top 500 — the CSV export contains everything.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
